
import { Component, Provide, Prop, Mixins } from "vue-property-decorator";
import { CreateElement } from "vue";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { affiliateTiersFilters } from "@/data/filters/affiliate";
import { affiliateTiersSorters } from "@/data/sorters/affiliate";

// Typings
import {
  IAffiliateTiersProviderData,
  IAffiliateTiersProviderMethods
} from "@/models/providers/affiliateTiers";
import { IAffiliateTier } from "@/models/affiliate";
import { IConfirmModalConfirmedEvent } from "@/models/modal";

@Component
export default class AffiliateTiersProvider extends Mixins(
  FilteringSortingTableStore
) {
  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Data
  public dataModule: DataModules = DataModules.AFFILIATES_TIERS;

  public filters = affiliateTiersFilters;
  public sorters = affiliateTiersSorters;

  // Lifecycle methods

  public async created() {
    if (!this.contentIsAvailable) return;
    this.getData();
  }

  public guardFunctionalities = ["list_affiliate_tiers"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Methods

  public manageAffiliateTier(affiliateTier: IAffiliateTier | null = null) {
    this.$store.dispatch(
      `data/${DataModules.AFFILIATES_TIERS}/openAddEditModal`,
      {
        props: {
          affiliateTier,
          isDisabled: this.$_.isNil(affiliateTier)
            ? !this.canCreate
            : !this.canUpdate,
          createAffiliateTier: this.createAffiliateTier,
          updateAffiliateTier: this.updateAffiliateTier
        },
        events: {
          success: () => this.refreshAffiliateTiers()
        }
      }
    );
  }

  public async deleteAffiliateTier(tierId: IAffiliateTier["id"]) {
    if (!this.canDelete) return;

    const modal = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        parent: this,
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_confirmation", {
            object: this.$t("_.affiliate_tier")
              .toString()
              .toLowerCase()
          })
        },
        canCancel: [],
        events: {
          confirmed: async ({
            stopProcessing
          }: IConfirmModalConfirmedEvent) => {
            try {
              await this.$store.dispatch(
                `data/${DataModules.AFFILIATES_TIERS}/remove`,
                {
                  id: tierId
                }
              );
              this.refreshAffiliateTiers();
              this.$buefy.toast.open({
                message: this.$t("_.object_successfully_removed", {
                  object: this.$t("_.affiliate_tier")
                }) as string
              });
              modal.close();
            } catch (error) {
              stopProcessing();
              this.$store.dispatch("api/handleError", error);
            }
          }
        }
      }
    });
  }

  public createAffiliateTier(data) {
    return this.$store.dispatch(`data/${DataModules.AFFILIATES_TIERS}/create`, {
      data
    });
  }

  public updateAffiliateTier(affiliateTierId: IAffiliateTier["id"], data) {
    return this.$store.dispatch(`data/${DataModules.AFFILIATES_TIERS}/update`, {
      id: affiliateTierId,
      data
    });
  }

  public refreshAffiliateTiers() {
    this.getData({ ignoreStored: true });
  }

  // Computed

  public get canCreate() {
    return this.$userCan("create_affiliate_tier");
  }

  public get canUpdate() {
    return this.$userCan("update_affiliate_tier");
  }

  public get canDelete() {
    return this.$userCan("delete_affiliate_tier");
  }

  // Life-cycle Methods

  public render(h: CreateElement) {
    if (!this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading },
        on: { click: () => this.refreshAffiliateTiers() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $affiliateTiersData: this.providerData(),
        $affiliateTiersMethods: this.providerMethods
      })
    );
  }

  // Provide

  @Provide("affiliateTiersProviderData")
  public providerData(): IAffiliateTiersProviderData {
    return {
      canCreate: this.canCreate,
      canUpdate: this.canUpdate,
      canDelete: this.canDelete,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      enablePagination: this.enablePagination,
      activeFilters: this.activeFilters,
      filters: this.filters,
      sorters: this.sorters,
      table: this.table
    };
  }

  @Provide("affiliateTiersProviderMethods")
  public get providerMethods(): IAffiliateTiersProviderMethods {
    return {
      manageAffiliateTier: this.manageAffiliateTier,
      deleteAffiliateTier: this.deleteAffiliateTier,
      createAffiliateTier: this.createAffiliateTier,
      updateAffiliateTier: this.updateAffiliateTier,
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onSort: this.onSort,
      onPageChange: this.onPageChange
    };
  }
}
